import { Injectable, inject, signal } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { formMarkAllAsTouched, scrollToFirstInvalidControl } from '@comparit/core';
import { NavigatorLinksSetup } from '../interfaces/page-navigator-setup.model';
import { firstInvalidStep } from '../misc/first-invalid-step';

export interface MenuLink {
  link: string;
}

export interface RevealBlockingStepOptions {
  /** Every step form group, keyed `step<n>`. Structural on purpose — no Sparte types in here. */
  allFgs: Record<string, AbstractControl>;
  /** The step the customer is on. */
  step: number;
  /** Route the steps hang off, e.g. `/formular`. */
  formularUrl: string;
}

@Injectable({ providedIn: 'root' })
export class PageNavigatorService {
  private readonly router = inject(Router);

  setup = signal<NavigatorLinksSetup | null>(null);
  stepsState = signal<boolean[]>([]);
  stepInteracted = signal(false);

  /**
   * Called from the forward button while `stepsState` is withholding the route: shows the customer
   * what is in the way, by marking the blocking step touched and scrolling its first invalid
   * control into view — going back to that step first when it is an earlier one.
   */
  revealBlockingStep({ allFgs, step, formularUrl }: RevealBlockingStepOptions): void {
    const blocking = firstInvalidStep(allFgs, step + 1);
    const fg = allFgs[blocking ?? `step${step}`];
    if (fg instanceof FormGroup) formMarkAllAsTouched(fg);

    if (!blocking || blocking === `step${step}`) {
      setTimeout(() => scrollToFirstInvalidControl());
      return;
    }

    void this.router.navigateByUrl(`${formularUrl}/${blocking}`).then(navigated => {
      if (navigated) setTimeout(() => scrollToFirstInvalidControl());
    });
  }
}
